/**
 * First-party visitor attribution + lightweight event tracker.
 *
 * Responsibilities:
 *   1. Mint a pseudonymous 365-day `_ac_visitor_id` cookie on the first
 *      page-view after the user accepts cookies via the consent banner.
 *   2. Mint a rolling 30-minute `_ac_session_id` cookie so we can split
 *      a returning visitor's behaviour into discrete sessions.
 *   3. Capture marketing metadata (UTMs, gclid, fbclid, msclkid),
 *      classify the attribution source (Direct / Organic Search /
 *      Organic Social / Paid Search / Paid Social / Referral / Email),
 *      and parse the User-Agent into browser / OS / device-type buckets.
 *   4. POST the payload to `/api/attribution/init` on first contact and
 *      `/api/attribution/session` on every new session window.
 *   5. Expose a global `window.trackEvent(name, props)` plus an ES
 *      named export so React components can fire custom events.
 *   6. Auto-track `page_view` on every React-Router URL change.
 *
 * Privacy / consent:
 *   The module reads `_ac_consent` first; anything other than
 *   `"accepted"` short-circuits all cookie writes and network calls.
 *   The consent banner is responsible for setting/clearing that cookie.
 */

const API_BASE = (
  typeof window !== "undefined" && window.location && window.location.origin
    ? window.location.origin
    : process.env.REACT_APP_BACKEND_URL
) + "/api";

const VISITOR_COOKIE = "_ac_visitor_id";
const SESSION_COOKIE = "_ac_session_id";
const CONSENT_COOKIE = "_ac_consent";
const FIRST_TOUCH_LS = "_ac_first_touch_v1";        // localStorage cache so
                                                     // we don't re-POST /init
                                                     // for the same visitor.
const SESSION_TTL_MIN = 30;
const VISITOR_TTL_DAYS = 365;

// ─────────────────────── consent gate ─────────────────────────────────

export function hasConsent() {
  return readCookie(CONSENT_COOKIE) === "accepted";
}

export function setConsent(value /* "accepted" | "essential" | "denied" */) {
  // 365-day consent record so the banner doesn't reappear on every visit.
  writeCookie(CONSENT_COOKIE, value, VISITOR_TTL_DAYS);
  // If the user revokes consent, wipe the analytic cookies immediately.
  if (value !== "accepted") {
    deleteCookie(VISITOR_COOKIE);
    deleteCookie(SESSION_COOKIE);
    try { localStorage.removeItem(FIRST_TOUCH_LS); } catch (_) { /* noop */ }
  }
}

// ─────────────────────── cookie helpers ───────────────────────────────

function writeCookie(name, value, days, { samesite = "Lax", secure = true } = {}) {
  if (typeof document === "undefined") return;
  const max = days * 24 * 60 * 60;
  const isLocal = window.location.protocol !== "https:" && window.location.hostname === "localhost";
  const flags = [
    `${name}=${encodeURIComponent(value)}`,
    `Path=/`,
    `Max-Age=${max}`,
    `SameSite=${samesite}`,
    // Browsers reject `Secure` cookies on http://localhost dev; skip it
    // there so the cookie still sets. Production is always https.
    isLocal ? null : (secure ? "Secure" : null),
  ].filter(Boolean);
  document.cookie = flags.join("; ");
}

function readCookie(name) {
  if (typeof document === "undefined") return null;
  const match = document.cookie.split("; ").find((c) => c.startsWith(`${name}=`));
  return match ? decodeURIComponent(match.split("=").slice(1).join("=")) : null;
}

function deleteCookie(name) {
  if (typeof document === "undefined") return;
  document.cookie = `${name}=; Path=/; Max-Age=0; SameSite=Lax`;
}

// ─────────────────────── id management ────────────────────────────────

function uuidv4() {
  // crypto.randomUUID is available in every browser we support (2022+).
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  // Fallback — same shape, weaker randomness, still spec-compliant.
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function getOrCreateVisitor() {
  let v = readCookie(VISITOR_COOKIE);
  let created = false;
  if (!v) {
    v = uuidv4();
    writeCookie(VISITOR_COOKIE, v, VISITOR_TTL_DAYS);
    created = true;
  }
  return { id: v, created };
}

function getOrCreateSession() {
  let s = readCookie(SESSION_COOKIE);
  let created = false;
  if (!s) {
    s = uuidv4();
    created = true;
  }
  // Always re-write — `Max-Age` resets the 30-min TTL so an active visitor
  // keeps the same session for as long as they engage.
  writeCookie(SESSION_COOKIE, s, SESSION_TTL_MIN / (24 * 60));
  return { id: s, created };
}

// ─────────────────────── metadata extraction ──────────────────────────

const SEARCH_HOSTS = [
  /(^|\.)google\./, /(^|\.)bing\./, /(^|\.)duckduckgo\./,
  /(^|\.)yahoo\./, /(^|\.)yandex\./, /(^|\.)ecosia\./, /(^|\.)brave\./,
];
const SOCIAL_HOSTS = [
  /(^|\.)facebook\./, /(^|\.)instagram\./, /(^|\.)linkedin\./,
  /(^|\.)twitter\./, /(^|\.)x\.com$/, /(^|\.)tiktok\./,
  /(^|\.)reddit\./, /(^|\.)youtube\./, /(^|\.)pinterest\./,
];

function classifyAttributionSource({ utm_medium, utm_source, gclid, fbclid, msclkid, referrer }) {
  const m = (utm_medium || "").toLowerCase();
  const s = (utm_source || "").toLowerCase();
  // Paid channels override everything — a click ID is unambiguous.
  if (gclid || /cpc|ppc|paidsearch/.test(m) || (/google/.test(s) && /cpc/.test(m))) return "Paid Search";
  if (msclkid || /bing/.test(s) && /cpc/.test(m)) return "Paid Search";
  if (fbclid || /paid_?social|paidsocial/.test(m)) return "Paid Social";
  if (/email|newsletter/.test(m)) return "Email";
  if (/social|organic_?social/.test(m)) return "Organic Social";
  // Referrer-based classification when no UTMs are present.
  if (!utm_medium && !utm_source) {
    try {
      if (!referrer) return "Direct";
      const host = new URL(referrer).hostname.toLowerCase();
      const own = (window.location.hostname || "").toLowerCase();
      if (host === own || host.endsWith(`.${own}`)) return "Direct";   // internal
      if (SEARCH_HOSTS.some((re) => re.test(host))) return "Organic Search";
      if (SOCIAL_HOSTS.some((re) => re.test(host))) return "Organic Social";
      return "Referral";
    } catch (_) {
      return "Direct";
    }
  }
  return "Referral";
}

function parseUA(ua) {
  const u = ua || (typeof navigator !== "undefined" ? navigator.userAgent : "");
  // Minimal UA parser — explicit ordering matters (Edge contains "Chrome").
  let browser_name = "Other", browser_version = "";
  const browserPatterns = [
    [/Edg\/([\d.]+)/, "Edge"],
    [/OPR\/([\d.]+)/, "Opera"],
    [/Chrome\/([\d.]+)/, "Chrome"],
    [/Firefox\/([\d.]+)/, "Firefox"],
    [/Version\/([\d.]+).*Safari/, "Safari"],
    [/MSIE ([\d.]+)/, "IE"],
  ];
  for (const [re, name] of browserPatterns) {
    const m = re.exec(u);
    if (m) { browser_name = name; browser_version = m[1]; break; }
  }
  let os = "Other";
  if (/Windows NT/.test(u)) os = "Windows";
  else if (/Mac OS X/.test(u)) os = "Mac OS";
  else if (/Android/.test(u)) os = "Android";
  else if (/iPhone|iPad|iPod/.test(u)) os = "iOS";
  else if (/Linux/.test(u)) os = "Linux";
  let device_type = "Desktop";
  if (/Tablet|iPad/.test(u)) device_type = "Tablet";
  else if (/Mobi|Android.*Mobile|iPhone|iPod/.test(u)) device_type = "Mobile";
  return { browser_name, browser_version, os, device_type };
}

function extractURLParams() {
  if (typeof window === "undefined") return {};
  const p = new URLSearchParams(window.location.search);
  const get = (k) => (p.get(k) || "").slice(0, 255) || null;
  return {
    utm_source:   get("utm_source"),
    utm_medium:   get("utm_medium"),
    utm_campaign: get("utm_campaign"),
    utm_term:     get("utm_term"),
    utm_content:  get("utm_content"),
    gclid:        get("gclid"),
    fbclid:       get("fbclid"),
    msclkid:      get("msclkid"),
  };
}

function buildPayload(visitorId, sessionId) {
  const url = extractURLParams();
  const ua = parseUA();
  const referrer = (typeof document !== "undefined" ? document.referrer : "") || null;
  const attribution_source = classifyAttributionSource({ ...url, referrer });
  return {
    visitor_id: visitorId,
    session_id: sessionId,
    ...url,
    referrer,
    attribution_source,
    initial_landing_page: typeof window !== "undefined" ? window.location.pathname + window.location.search : null,
    ...ua,
  };
}

// ─────────────────────── network helpers ──────────────────────────────

async function postJSON(path, body, { keepalive = false } = {}) {
  try {
    await fetch(`${API_BASE}${path}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      credentials: "same-origin",
      keepalive,
    });
  } catch (_) { /* swallow — analytics must never break the page */ }
}

// ─────────────────────── public API ───────────────────────────────────

let _initPromise = null;
let _activeVisitor = null;
let _activeSession = null;

/**
 * Bootstrap the tracker. Idempotent — safe to call from a top-level
 * effect on every route change. Returns the active {visitor, session}
 * pair once consent has been granted, otherwise resolves to null.
 */
export async function bootAttribution() {
  if (!hasConsent()) return null;
  if (_initPromise) return _initPromise;

  _initPromise = (async () => {
    const visitor = getOrCreateVisitor();
    const session = getOrCreateSession();
    _activeVisitor = visitor.id;
    _activeSession = session.id;

    const payload = buildPayload(visitor.id, session.id);

    // First-ever contact for this browser → /init writes BOTH first- and
    // last-touch (backend uses $setOnInsert so first-touch is immutable).
    if (visitor.created || !readLocalFlag(FIRST_TOUCH_LS)) {
      await postJSON("/attribution/init", payload);
      writeLocalFlag(FIRST_TOUCH_LS, "1");
    } else if (session.created) {
      // Returning visitor, new 30-min window → only last-touch is updated.
      await postJSON("/attribution/session", payload);
    } else {
      // Same session, just a re-render — nothing to write.
    }

    return { visitor: visitor.id, session: session.id };
  })();

  return _initPromise;
}

/**
 * Fire a custom event. Non-blocking; keeps cookies alive past
 * `unload` via `keepalive: true` so form-submit redirects don't lose
 * the event in flight.
 */
export function trackEvent(eventName, properties = {}) {
  if (!hasConsent()) return;
  if (!_activeVisitor || !_activeSession) {
    // Race: caller fired an event before bootAttribution() resolved.
    // Re-init synchronously from cookies (no network).
    _activeVisitor = readCookie(VISITOR_COOKIE);
    _activeSession = readCookie(SESSION_COOKIE);
    if (!_activeVisitor || !_activeSession) return;
  }
  const body = {
    visitor_id: _activeVisitor,
    session_id: _activeSession,
    event_name: String(eventName).slice(0, 120),
    page_url: window.location.href,
    event_properties: properties || {},
  };
  postJSON("/attribution/event", body, { keepalive: true });
}

/**
 * Convenience: track a `page_view` for the current URL. Called from
 * the React-Router listener mounted in App.js.
 */
export function trackPageView() {
  trackEvent("page_view", {
    path: window.location.pathname,
    search: window.location.search,
    title: typeof document !== "undefined" ? document.title : "",
  });
}

/** Read current ids (e.g. to attach to a lead payload). May be null. */
export function getVisitorId() { return _activeVisitor || readCookie(VISITOR_COOKIE); }
export function getSessionId() { return _activeSession || readCookie(SESSION_COOKIE); }

// localStorage helpers — wrapped so we can no-op in privacy mode where
// localStorage throws on access.
function readLocalFlag(key)        { try { return localStorage.getItem(key); } catch (_) { return null; } }
function writeLocalFlag(key, value){ try { localStorage.setItem(key, value); } catch (_) { /* noop */ } }

// Wire `window.trackEvent` for legacy scripts / tag managers that expect
// a global. Component-level imports should prefer the named export.
if (typeof window !== "undefined") {
  window.trackEvent = trackEvent;
}
